import React, { useEffect, useState } from 'react'

import { api, type CommunityActorContextRecord, type CommunityProfileRecord } from '../utils/api'
import { useT } from '../i18n'

const getMembershipBadgeStyle = (membershipStatus: 'invited' | 'active' | 'revoked') => {
  if (membershipStatus === 'active') {
    return { backgroundColor: '#dcfce7', color: '#166534' }
  }

  if (membershipStatus === 'invited') {
    return { backgroundColor: '#fef3c7', color: '#92400e' }
  }

  return { backgroundColor: '#fee2e2', color: '#991b1b' }
}

export default function CommunityProfile() {
  const t = useT()
  const [profile, setProfile] = useState<CommunityProfileRecord | null>(null)
  const [actorContext, setActorContext] = useState<CommunityActorContextRecord | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [publicBio, setPublicBio] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError('')
      try {
        const [result, resolvedActorContext] = await Promise.all([
          api.communityProfile.get(),
          api.communityActorContext.get()
        ])
        if (cancelled) return
        setProfile(result)
        setActorContext(resolvedActorContext)
        setDisplayName(result.displayName ?? '')
        setPublicBio(result.publicBio ?? '')
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : t('communityProfile.error.loadFailed'))
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [t])

  const saveProfile = async () => {
    setIsSaving(true)
    setError('')
    setStatus('')
    try {
      const result = await api.communityProfile.update({
        displayName,
        publicBio
      })
      setProfile(result)
      setActorContext((prev) => prev ? {
        ...prev,
        communityProfileId: result.communityProfileId,
        communityDisplayName: result.displayName,
        publicBio: result.publicBio
      } : prev)
      setDisplayName(result.displayName ?? '')
      setPublicBio(result.publicBio ?? '')
      setStatus(t('communityProfile.status.saved'))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('communityProfile.error.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  let identityContent: React.ReactNode = null
  if (isLoading) {
    identityContent = <div style={{ color: '#6b7280' }}>{t('communityProfile.loading')}</div>
  } else if (profile) {
    identityContent = (
      <div style={{ display: 'grid', gap: 8 }}>
        <div><b>{t('communityProfile.identity.accountName')}</b> {profile.accountDisplayName}</div>
        <div><b>{t('communityProfile.identity.actor')}</b> {profile.actorId}</div>
        <div><b>{t('communityProfile.identity.role')}</b> {profile.membershipRole}</div>
        <div><b>{t('communityProfile.identity.membership')}</b> {profile.tenantMembershipId}</div>
      </div>
    )
  }

  let accessContent: React.ReactNode = null
  if (isLoading) {
    accessContent = <div style={{ color: '#6b7280' }}>{t('communityProfile.loading')}</div>
  } else if (actorContext) {
    accessContent = (
      <>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#eff6ff', color: '#1d4ed8' }}>
            {t('communityProfile.access.privateContext')}: {actorContext.tenantKey}
          </span>
          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#eef2ff', color: '#3730a3' }}>
            {t('communityProfile.access.membershipRole')}: {actorContext.membershipRole}
          </span>
          <span data-testid="community-profile-active-group-count" style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#dcfce7', color: '#166534' }}>
            {t('communityProfile.access.activeGroups')}: {actorContext.activeGroupContextCount}
          </span>
          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#fef3c7', color: '#92400e' }}>
            {t('communityProfile.access.invitedGroups')}: {actorContext.invitedGroupContextCount}
          </span>
          <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b' }}>
            {t('communityProfile.access.revokedGroups')}: {actorContext.revokedGroupContextCount}
          </span>
        </div>

        {actorContext.groupActorContexts.length === 0 ? (
          <div data-testid="community-profile-access-empty" style={{ color: '#6b7280', marginTop: 12 }}>
            {t('communityProfile.access.empty')}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            {actorContext.groupActorContexts.map((groupContext) => {
              const membershipBadgeStyle = getMembershipBadgeStyle(groupContext.membershipStatus)
              return (
                <div
                  key={groupContext.groupMembershipId}
                  data-testid={`community-profile-group-context-${groupContext.groupId}`}
                  style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, backgroundColor: '#fcfcfd', display: 'grid', gap: 8 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>{groupContext.groupDisplayName}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{t('communityProfile.access.groupId')}: {groupContext.groupId}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, backgroundColor: '#eef2ff', color: '#3730a3' }}>
                        {t('communityProfile.access.groupRole')}: {t(`ownerConsole.groups.role.${groupContext.groupRole}` as const)}
                      </span>
                      <span style={{ padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, ...membershipBadgeStyle }}>
                        {t('communityProfile.access.membershipStatus')}: {t(`ownerConsole.groups.membershipStatus.${groupContext.membershipStatus}` as const)}
                      </span>
                    </div>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>
                    {t('communityProfile.access.groupMembershipId')}: {groupContext.groupMembershipId}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>
                    {t('communityProfile.access.partnerOrganization')}: {groupContext.partnerOrganizationName ?? t('communityProfile.access.none')}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </>
    )
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>{t('communityProfile.title')}</h2>
      <p style={{ color: '#6b7280', marginTop: 8 }}>{t('communityProfile.subtitle')}</p>

      {error && (
        <div style={{ padding: 12, borderRadius: 8, marginTop: 12, backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
          {error}
        </div>
      )}

      {status && (
        <div data-testid="community-profile-status" style={{ padding: 12, borderRadius: 8, marginTop: 12, backgroundColor: '#ecfdf5', border: '1px solid #bbf7d0', color: '#166534' }}>
          {status}
        </div>
      )}

      <div style={{ marginTop: 18, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <h3 style={{ marginTop: 0 }}>{t('communityProfile.identity.title')}</h3>
        {identityContent}
      </div>

      <div style={{ marginTop: 18, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <h3 style={{ marginTop: 0 }}>{t('communityProfile.access.title')}</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('communityProfile.access.subtitle')}</p>
        {accessContent}
      </div>

      <div style={{ marginTop: 18, padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', backgroundColor: 'white' }}>
        <h3 style={{ marginTop: 0 }}>{t('communityProfile.editor.title')}</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>{t('communityProfile.editor.subtitle')}</p>

        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('communityProfile.editor.displayNameLabel')}</span>
            <input
              data-testid="community-profile-display-name-input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder={t('communityProfile.editor.displayNamePlaceholder')}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
              disabled={isLoading || isSaving}
              maxLength={120}
            />
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('communityProfile.editor.displayNameHint')}</span>
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{t('communityProfile.editor.publicBioLabel')}</span>
            <textarea
              data-testid="community-profile-public-bio-input"
              value={publicBio}
              onChange={(event) => setPublicBio(event.target.value)}
              placeholder={t('communityProfile.editor.publicBioPlaceholder')}
              style={{ minHeight: 140, padding: 10, borderRadius: 8, border: '1px solid #d1d5db', resize: 'vertical' }}
              disabled={isLoading || isSaving}
              maxLength={500}
            />
            <span style={{ color: '#6b7280', fontSize: 12 }}>{t('communityProfile.editor.publicBioHint')}</span>
          </label>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="community-profile-save-button"
            onClick={() => void saveProfile()}
            disabled={isLoading || isSaving}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#111827',
              color: 'white',
              fontWeight: 800,
              cursor: (isLoading || isSaving) ? 'not-allowed' : 'pointer'
            }}
          >
            {isSaving ? t('communityProfile.action.saving') : t('communityProfile.action.save')}
          </button>
          <span style={{ color: '#6b7280', fontSize: 12 }}>{t('communityProfile.editor.note')}</span>
        </div>
      </div>
    </div>
  )
}